/**
 * @file ShipmentEventsFeed.tsx
 * @description Лента событий доставки: строки, свёртки, диагностика
 * (phase19.13 шаг 6).
 *
 * Отвечает только за показ: что сворачивать и что резать — решают чистые
 * функции `shipment-events-view.ts`, данные приносит `useShipmentEvents`.
 */

import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { ShipmentEventRawSheet } from "./ShipmentEventRawSheet";
import { ShipmentEventRow } from "./ShipmentEventRow";
import { Button } from "../ui/button";
import { useIsCompactLayout } from "../../hooks/useIsCompactLayout";
import type { ShipmentEventDto } from "../../lib/api-delivery-monitoring";
import {
  buildShipmentEventRows,
  transitGroupLabel,
  visibleShipmentEvents,
} from "../../lib/shipment-events-view";
import { shopTimeZoneNote } from "../../lib/shop-time";

interface Props {
  /** События новыми сверху (порядок сервера). */
  items: ShipmentEventDto[];
  /** Отгрузка ещё в пути — только для неё имеет смысл ругаться на вебхук. */
  active: boolean;
  carrierSettingsHref: string;
}

export function ShipmentEventsFeed({ items, active, carrierSettingsHref }: Props) {
  const compact = useIsCompactLayout();
  const [showAll, setShowAll] = useState(false);
  const [openGroups, setOpenGroups] = useState<string[]>([]);
  const [rawEvent, setRawEvent] = useState<ShipmentEventDto | null>(null);

  const { visible, hiddenCount } = visibleShipmentEvents(items, showAll);
  const rows = buildShipmentEventRows(visible);
  // Все события пришли опросом — значит подписка на вебхук не работает.
  const syncOnly = active && items.length > 0 && items.every((e) => e.source === "SYNC");

  const toggleGroup = (key: string): void =>
    setOpenGroups((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );

  return (
    <div className="flex flex-col gap-2">
      <ol
        className={
          compact
            ? "relative flex flex-col gap-3 pl-8 before:absolute before:top-2 before:bottom-2 before:left-3 before:w-px before:bg-on-surface/12"
            : "flex flex-col divide-y divide-outline-variant"
        }
      >
        {rows.map((row) => {
          if (row.kind === "event") {
            return (
              <ShipmentEventRow
                key={row.key}
                event={row.event}
                compact={compact}
                onShowRaw={setRawEvent}
              />
            );
          }
          const expanded = openGroups.includes(row.key);
          return (
            <li key={row.key} className={compact ? "relative" : "py-1"}>
              <button
                type="button"
                onClick={() => toggleGroup(row.key)}
                aria-expanded={expanded}
                className="flex min-h-11 w-full items-center gap-1.5 rounded-md px-1 text-left text-sm text-on-surface-variant hover:bg-surface-low focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              >
                {expanded ? (
                  <ChevronDown className="size-4 shrink-0" />
                ) : (
                  <ChevronRight className="size-4 shrink-0" />
                )}
                <span className="min-w-0 break-words">{transitGroupLabel(row)}</span>
              </button>
              {expanded ? (
                <ol
                  className={
                    compact
                      ? "mt-2 flex flex-col gap-3 pl-2"
                      : "flex flex-col divide-y divide-outline-variant"
                  }
                >
                  {row.events.map((event) => (
                    <ShipmentEventRow
                      key={event.id}
                      event={event}
                      compact={compact}
                      onShowRaw={setRawEvent}
                    />
                  ))}
                </ol>
              ) : null}
            </li>
          );
        })}
      </ol>

      {hiddenCount > 0 ? (
        <Button
          size="sm"
          variant="ghost"
          className="min-h-11 self-start"
          onClick={() => setShowAll(true)}
        >
          Показать все {items.length}
        </Button>
      ) : null}

      {syncOnly ? (
        <p className="rounded-md bg-warning/12 px-2 py-1.5 text-xs">
          Ни одного события по вебхуку — статусы приходят только опросом.{" "}
          <Link to={carrierSettingsHref} className="text-primary hover:underline">
            Проверьте подписку в настройках СДЭК
          </Link>
          .
        </p>
      ) : null}

      <p className="text-xs text-on-surface-variant">{shopTimeZoneNote()}</p>

      <ShipmentEventRawSheet event={rawEvent} onClose={() => setRawEvent(null)} />
    </div>
  );
}
